import "@xyflow/react/dist/style.css";

import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { ReactFlow, ReactFlowProvider, useReactFlow } from "@xyflow/react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, Handshake } from "lucide-react";

import type { useMatchFlow } from "@/features/match/useMatchFlow";
import {
  buildKnockoutView,
  edgeTypes,
  nodeTypes,
} from "@/features/tournament/knockout/KnockoutView";
import {
  type KnockoutTournamentActorRef,
  type TournamentActorRef,
} from "@/features/tournament/tournamentAdapters";
import { TOURNAMENT_FORMAT_KEYS, useTranslations } from "@/i18n";
import { usePlayers } from "@/store/selectors";
import { isTournament, TournamentFormat } from "@/types";
import type { PlayerId } from "@/types/branded";
import { theme } from "@/ui/theme";
import type { KnockoutMatchActorRef } from "../tournament/knockout/knockout.machine";
import type { MatchId } from "../tournament/types";
import type { KnockoutGraph } from "../tournament/types/graph";

// ── Styled ───────────────────────────────────────────────────

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0 2px;
`;

const StageTag = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${theme.colors.textMuted};
`;

const Title = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: 1.875rem;
  line-height: 1.1;
`;

const FormatLabel = styled.p`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
  margin-top: 0.25rem;
`;

const MatchCardEl = styled.div<{ isCurrent?: boolean }>`
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent);
  padding: 0.75rem;
  box-shadow: ${theme.shadow.card};

  ${(p) =>
    p.isCurrent &&
    `
    outline: 2px solid color-mix(in oklab, ${theme.colors.primary} 60%, transparent);
    box-shadow: ${theme.shadow.glow};
  `}
`;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.78rem;
  color: ${theme.colors.textMuted};
`;

const MatchGrid = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PlayerBtn = styled.button<{ isWinner?: boolean }>`
  all: unset;
  cursor: pointer;
  flex: 1;
  padding: 0.5rem 0.75rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${(p) => (p.isWinner ? theme.colors.primary : theme.colors.border)};
  background: ${(p) =>
    p.isWinner
      ? `color-mix(in oklab, ${theme.colors.primary} 10%, transparent)`
      : `color-mix(in oklab, ${theme.colors.bgCard} 60%, transparent)`};
  font-family: ${theme.fonts.display};
  font-size: 0.875rem;
  text-align: center;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: color-mix(in oklab, ${theme.colors.primary} 50%, transparent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const VsLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
`;

const DrawBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.375rem;
  border-radius: ${theme.radii.lg};
  background: color-mix(in oklab, ${theme.colors.secondary} 60%, transparent);
  font-size: 0.75rem;
  font-weight: 600;
  width: 100%;
  color: ${theme.colors.textMuted};
  &:hover {
    background: ${theme.colors.secondary};
  }
`;

const StandingsWrap = styled.div`
  overflow: hidden;
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent);
  box-shadow: ${theme.shadow.card};
`;

const StandingsHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr repeat(5, 1.5rem);
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${theme.colors.textMuted};
`;

const StandingsRow = styled.li<{ isFirst?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr repeat(5, 1.5rem);
  align-items: center;
  gap: 0.5rem;
  border-top: 1px solid ${theme.colors.border};
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;

  ${(p) => p.isFirst && `background: color-mix(in oklab, ${theme.colors.primary} 5%, transparent);`}
`;

const CollapsibleRow = styled(motion.div)`
  overflow: hidden;
`;

const StandingsRank = styled.span`
  font-family: ${theme.fonts.display};
  font-size: 1rem;
  color: ${theme.colors.textMuted};
`;

const StandingsStat = styled.span`
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const StandingsStatBold = styled(StandingsStat)`
  font-family: ${theme.fonts.display};
`;

const StandingsToggle = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  padding: 0.5rem;
  border-top: 1px solid ${theme.colors.border};
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${theme.colors.textMuted};
  transition: background 0.15s;

  &:hover {
    background: color-mix(in oklab, ${theme.colors.primary} 5%, transparent);
  }
`;

const WaitingMsg = styled.p`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
  padding: 1rem;
  text-align: center;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radii["2xl"]};
`;

// ── Bracket view ─────────────────────────────────────────────

const BracketWrap = styled.div`
  width: 100%;
  // height: 350px;
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid ${theme.colors.border};
  overflow: hidden;
  background: ${theme.colors.bg};
  aspect-ratio: 13 / 6;
`;

// ── Bracket component (knockout only, ReactFlow) ─────────────

const isKnockoutRef = (ref: TournamentActorRef): ref is KnockoutTournamentActorRef => {
  const snap = ref.getSnapshot();
  return !!(snap.context && "graph" in snap.context && snap.context.graph?.nodes);
};

const easeInOut = (foo: number) => (t: number) =>
  t < 0.5 ? Math.pow(2, foo - 1) * Math.pow(t, foo) : 1 - Math.pow(-2 * t + 2, foo) / 2;

const BracketInner = ({
  graph,
  matchRefs,
  activeMatchId,
  playerName,
}: {
  graph: KnockoutGraph;
  matchRefs: Map<MatchId, KnockoutMatchActorRef>;
  activeMatchId?: MatchId;
  playerName: (pid: PlayerId | null | undefined) => string;
}) => {
  const reactFlow = useReactFlow();

  const { nodes, edges } = useMemo(
    () => buildKnockoutView(graph, matchRefs, playerName),
    [matchRefs, playerName],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!activeMatchId) {
        reactFlow.fitView({ duration: 1500 });
        return;
      }

      const node = nodes.find((n) => n.id === activeMatchId);
      if (node) {
        reactFlow.setCenter(node.position.x + (105 * 13) / 6 / 2, node.position.y + 105 / 2, {
          zoom: 1,
          duration: 1000,
          ease: easeInOut(2.5),
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [activeMatchId, nodes, reactFlow]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      proOptions={{ hideAttribution: true }}
      fitView
      edgesReconnectable={false}
      nodesDraggable={false}
      minZoom={0}
      maxZoom={1.5}
    />
  );
};

// ── Component ────────────────────────────────────────────────

type FlowApi = ReturnType<typeof useMatchFlow>;

export const TournamentStage = ({ flow }: { flow: FlowApi }) => {
  const { t } = useTranslations();
  const playersById = usePlayers();

  const {
    activity,
    tournamentRef,
    activeTournamentMatch,
    tournamentStandings,
    canSubmitTournamentResults,
    recordTournamentMatch,
  } = flow;

  const [standingsExpanded, setStandingsExpanded] = useState(false);

  useEffect(() => {
    if (canSubmitTournamentResults) {
      setStandingsExpanded(true);
    }
  });

  if (!activity || !isTournament(activity)) return null;

  const formatLabel = t(TOURNAMENT_FORMAT_KEYS[activity.tournamentFormat]);

  const playerName = (pid: PlayerId | null | undefined) => {
    if (pid === null) return t("arena.bye");
    if (pid === undefined) return t("arena.tbd");
    return playersById[pid]?.name ?? pid;
  };

  const isKnockout =
    activity.tournamentFormat === TournamentFormat.SingleElimination ||
    activity.tournamentFormat === TournamentFormat.DoubleElimination;

  const activeMatchLabel =
    activeTournamentMatch == null
      ? null
      : activeTournamentMatch.round != null
        ? t("arena.roundN", activeTournamentMatch.round)
        : activeTournamentMatch.isChampionship
          ? t("arena.championship")
          : t("arena.bracketN", (activeTournamentMatch.bracket ?? 0) + 1);

  const [playerA, playerB] = activeTournamentMatch?.players ?? [undefined, undefined];

  const bracketView = useMemo(() => {
    if (!isKnockout || !tournamentRef || !isKnockoutRef(tournamentRef)) return null;

    const { graph, matchRefs } = tournamentRef.getSnapshot().context;

    return (
      <BracketWrap>
        <ReactFlowProvider>
          <BracketInner
            graph={graph}
            matchRefs={matchRefs}
            activeMatchId={activeTournamentMatch?.id}
            playerName={playerName}
          />
        </ReactFlowProvider>
      </BracketWrap>
    );
  }, [isKnockout, tournamentRef, activeTournamentMatch, playerName]);

  return (
    <Content>
      <header>
        <StageTag>{t("arena.stageTournament")}</StageTag>
        <Title>{activity.name}</Title>
        <FormatLabel>{formatLabel}</FormatLabel>
      </header>

      {/* Bracket view for knockout */}
      {isKnockout && tournamentRef && bracketView}

      {/* Standings for round-robin / swiss */}
      {!isKnockout && tournamentStandings.length > 0 && (
        <StandingsWrap>
          <StandingsHeader>
            <span>#</span>
            <span>{t("arena.standingsPlayer")}</span>
            <span style={{ textAlign: "center" }}>{t("arena.standingsMP")}</span>
            <span style={{ textAlign: "center" }}>{t("arena.standingsW")}</span>
            <span style={{ textAlign: "center" }}>{t("arena.standingsD")}</span>
            <span style={{ textAlign: "center" }}>{t("arena.standingsL")}</span>
            <span style={{ textAlign: "center" }}>{t("arena.standingsPts")}</span>
          </StandingsHeader>
          <ul style={{ listStyle: "none" }}>
            {tournamentStandings.slice(0, 3).map((stats, i) => (
              <StandingsRow key={stats.id} isFirst={i === 0}>
                <StandingsRank>{i + 1}</StandingsRank>
                <span
                  style={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {playerName(stats.id)}
                </span>
                <StandingsStat>{stats.opponents.length}</StandingsStat>
                <StandingsStat>{stats.wins}</StandingsStat>
                <StandingsStat>{stats.draws}</StandingsStat>
                <StandingsStat>{stats.losses}</StandingsStat>
                <StandingsStatBold>{stats.points}</StandingsStatBold>
              </StandingsRow>
            ))}
            <AnimatePresence initial={false}>
              {standingsExpanded &&
                tournamentStandings.slice(3).map((stats, i) => (
                  <CollapsibleRow
                    key={stats.id}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      duration: canSubmitTournamentResults ? 0.75 : 0.25,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                  >
                    <StandingsRow isFirst={false}>
                      <StandingsRank>{i + 4}</StandingsRank>
                      <span
                        style={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {playerName(stats.id)}
                      </span>
                      <StandingsStat>{stats.opponents.length}</StandingsStat>
                      <StandingsStat>{stats.wins}</StandingsStat>
                      <StandingsStat>{stats.draws}</StandingsStat>
                      <StandingsStat>{stats.losses}</StandingsStat>
                      <StandingsStatBold>{stats.points}</StandingsStatBold>
                    </StandingsRow>
                  </CollapsibleRow>
                ))}
            </AnimatePresence>
          </ul>
          {tournamentStandings.length > 3 && (
            <AnimatePresence initial={canSubmitTournamentResults}>
              {!canSubmitTournamentResults && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.75 }}
                >
                  <StandingsToggle onClick={() => setStandingsExpanded((v) => !v)}>
                    {standingsExpanded ? (
                      <>
                        {t("arena.showLess")} <ChevronUp size={14} />
                      </>
                    ) : (
                      <>
                        {t("arena.showAll", tournamentStandings.length)} <ChevronDown size={14} />
                      </>
                    )}
                  </StandingsToggle>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </StandingsWrap>
      )}

      {/* Active match */}
      {activeTournamentMatch ? (
        <MatchCardEl isCurrent>
          <MatchHeader>
            <strong>{activeMatchLabel}</strong>
            <span>{t("arena.ready")}</span>
          </MatchHeader>

          <MatchGrid>
            <PlayerBtn
              disabled={playerA == null}
              isWinner={false}
              onClick={() => recordTournamentMatch(activeTournamentMatch.id, playerA!)}
            >
              {playerName(playerA)}
            </PlayerBtn>
            <VsLabel>{t("general.vs")}</VsLabel>
            <PlayerBtn
              disabled={playerB == null}
              isWinner={false}
              onClick={() => recordTournamentMatch(activeTournamentMatch.id, playerB!)}
            >
              {playerName(playerB)}
            </PlayerBtn>
          </MatchGrid>

          {activeTournamentMatch.canDraw && (
            <DrawBtn onClick={() => recordTournamentMatch(activeTournamentMatch.id, "draw")}>
              <Handshake size={14} /> {t("arena.draw")}
            </DrawBtn>
          )}
        </MatchCardEl>
      ) : (
        <WaitingMsg>
          {canSubmitTournamentResults
            ? t("arena.tournamentCompleteMsg")
            : t("arena.waitingNextMatch")}
        </WaitingMsg>
      )}
    </Content>
  );
};
